<template>
  <layout-default>
    <div
      v-if="loading"
      class="container loader-wrapper d-flex justify-content-center align-items-center"
    >
      <div class="loader"></div>
    </div>
    <div v-else class="container product-page">
      <div class="product-header">
        <Breadcrumb class="product-breadcrumb" />
        <h1 v-if="product.nome" class="product-title">{{ product.nome }}</h1>
        <p v-if="productSubtitle" class="product-subtitle">{{ productSubtitle }}</p>
      </div>

      <div class="row product-main">
        <div class="col-lg-6 col-md-12 product-main__gallery">
          <div class="product-gallery-card">
            <product-slider class="product-image" :photos="productPhotos"></product-slider>
          </div>
        </div>
        <div class="col-lg-6 col-md-12 product-main__summary">
          <div class="product-summary-card">
            <div class="summary-price-block" v-if="displayPrice !== null">
              <span class="summary-label">{{ priceLabel }}</span>
              <span class="summary-value">{{ $convertCurrency(displayPrice) }}</span>
            </div>
            <div
              v-if="productType === 'auction' && bidDetail.dataFine"
              class="summary-countdown"
            >
              <div class="summary-countdown__label">
                <span class="summary-label">{{$t('product.timeLeft')}}</span>
                <span class="summary-countdown__date text-secondary">
                  Auction {{ didAuctionEnd ? "ends" : "ended" }}:
                  {{ $formatDate(bidDetail.dataFine) }}
                </span>
              </div>
              <Counter
                class="summary-counter"
                :endDate="new Date(bidDetail.dataFine)"
                counterInnerClass="w-100"
              />
            </div>
            <div
              v-if="productVariant.descrizione"
              class="summary-description text-secondary"
              v-html="productVariant.descrizione"
            ></div>

            <ul class="summary-meta list-unstyled">
              <li v-if="itemCondition" class="summary-meta__item">
                <span class="meta-label">Item condition</span>
                <span class="meta-value">{{ itemCondition }}</span>
              </li>
              <li v-if="productVariant.codice" class="summary-meta__item">
                <span class="meta-label">SKU</span>
                <span class="meta-value">{{ productVariant.codice }}</span>
              </li>
              <li v-if="productCategories.length" class="summary-meta__item">
                <span class="meta-label">Categories</span>
                <FilterListTags
                  :comma="true"
                  class="meta-tags"
                  :items="productCategories"
                />
              </li>
              <li v-if="productTags.length" class="summary-meta__item">
                <span class="meta-label">Tags</span>
                <FilterListTags
                  :comma="true"
                  class="meta-tags"
                  :items="productTags"
                />
              </li>
            </ul>

            <div class="summary-actions">
              <div class="summary-quantity">
                <span class="summary-label">
                  {{ productType === "auction" ? "Your bid" : "Quantity" }}
                </span>
                <BasicInputNumberStep v-model="val" />
              </div>
              <BidIcons
                :vId="productVariant._id"
                :pId="product._id"
                :type="productType"
                :horizontal="true"
                class="summary-action-icons"
                :didAuctionEnd="didAuctionEnd"
                @bid-icon-clicked="placeBid"
                @add-to-cart-clicked="handleAddToCart"
              />
            </div>

            <div v-if="shareLinks.length" class="summary-share">
              <span class="meta-label">Share</span>
              <ul class="share-list">
                <li v-for="link in shareLinks" :key="link.name">
                  <a
                    :href="link.url"
                    class="share-icon"
                    :class="link.className"
                    target="_blank"
                    rel="noopener"
                  >
                    <font-awesome-icon :icon="link.icon" />
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div class="product-tabs">
        <tab-wrapper>
          <tab :title="$t('product.description')">
            <Description
              class="mt-4"
              :description="productVariant.descrizione"
            />
          </tab>
          <tab :title="$t('product.reviews')">
            <Reviews class="mt-4" :productId="product._id" />
          </tab>
          <tab v-if="productType === 'auction'" :title="'Auction History'">
            <TableAuctionHistory
              class="mt-4"
              :latestAuctionOffers="latestAuctionOffers"
            />
          </tab>
          <tab :title="$t('product.moreFromVendor')">
            <div class="row mt-4">
              <div
                v-for="auction in productAllVariant"
                :key="auction._id"
                class="col-6 col-md-3 mb-4"
              >
                <ProductCard
                  v-bind="auction"
                  :endDate="new Date(auction.endDate)"
                />
              </div>
            </div>
          </tab>
        </tab-wrapper>
      </div>

      <div class="related-products">
        <div class="related-products__header">
          <h2 class="related-products__title">{{$t('product.relatedProducts')}}</h2>
        </div>
        <div class="row related-products__list">
          <div
            v-for="auction in productAllVariant"
            :key="`related-${auction._id}`"
            class="col-12 col-sm-6 col-lg-3 mb-4"
          >
            <ProductCard
              v-bind="auction"
              :endDate="new Date(auction.endDate)"
            />
          </div>
        </div>
      </div>
    </div>
  </layout-default>
</template>

<script>
import LayoutDefault from "../../components/organisms/LayoutDefault/LayoutDefault.vue";
import TabWrapper from "../../components/molecules/Tab/TabWrapper.vue";
import Tab from "../../components/molecules/Tab/TabContent.vue";
import Counter from "../../components/atoms/Counter/Counter.vue";
import FilterListTags from "../../components/atoms/Filter/FilterListTags.vue";
import Breadcrumb from "../../components/atoms/Breadcrumb/Breadcrumb.vue";
import TableAuctionHistory from "../../components/atoms/Product/ProductTableAuctionHistory.vue";
import Description from "../../components/atoms/Product/ProductDescription.vue";
import ProductCard from "../../components/molecules/Product/ProductCard.vue";
import ProductSlider from "../../components/atoms/Carousel/ProductSlider.vue";
import BidIcons from "../../components/atoms/Button/ButtonIconBids.vue";
import BasicInputNumberStep from "../../components/atoms/FormInput/BasicInputNumberStep.vue";
import Reviews from "../../components/atoms/Product/ProductReviews.vue";
import {
  addBidService,
  getBidDetailService,
  getLatestAuctionOffersService,
} from "../../services/auctionService";
import { LOGGED_IN_USER } from "../../constents/app";
import { getAllProductsAndVariants } from "../../composables/manageProducts";
import { addToCart } from "../../composables/manageCart";

const toNumber = (value) => {
  if (value === null || value === undefined || value === "") {
    return null;
  }
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : null;
};

export default {
  components: {
    LayoutDefault,
    ProductSlider,
    Tab,
    TabWrapper,
    Counter,
    BidIcons,
    BasicInputNumberStep,
    FilterListTags,
    Breadcrumb,
    TableAuctionHistory,
    Description,
    Reviews,
    ProductCard,
  },
  data() {
    return {
      loading: true,
      val: 1,
      allProducts: [],
      auctions: [],
      productVariant: {},
      productAllVariant: [],
      productType: "",
      product: {},
      astaId: "",
      bidDetail: {},
      imgs: [],
      latestAuctionOffers: [],
      shareUrl: "",
    };
  },
  computed: {
    didAuctionEnd() {
      if (this.productType === "auction") {
        const end = this.bidDetail?.dataFine;
        return end ? new Date(end) > new Date() : false;
      }
      return false;
    },
    productPhotos() {
      return (this.imgs || [])
        .map((photo) => (photo?.path ? photo.path : photo))
        .filter(Boolean);
    },
    priceLabel() {
      if (this.productType === "auction") {
        return this.didAuctionEnd ? "Current bid" : "Product sold for";
      }
      return "Price";
    },
    displayPrice() {
      if (this.productType === "auction") {
        const candidates = [
          this.latestAuctionOffers?.[0]?.prezzo,
          this.bidDetail?.prezzoRiserva,
          this.productVariant?.prezzo,
        ]
          .map(toNumber)
          .filter((value) => value !== null);
        return candidates.length ? candidates[0] : null;
      }
      return toNumber(this.productVariant?.prezzo);
    },
    productSubtitle() {
      const product = this.product || {};
      return (
        product?.autore ||
        product?.author ||
        product?.designer ||
        product?.brand ||
        product?.brandName ||
        product?.vendorName ||
        product?.vendor ||
        product?.azienda ||
        ""
      );
    },
    itemCondition() {
      return (
        this.productVariant?.condizione ||
        this.productVariant?.condition ||
        this.product?.condizione ||
        this.product?.condition ||
        ""
      );
    },
    productCategories() {
      const raw =
        this.productVariant?.categorie ??
        this.productVariant?.categories ??
        this.product?.categorie ??
        this.product?.categories ??
        [];
      if (!Array.isArray(raw)) {
        return [];
      }
      return raw
        .map((item) => (typeof item === "string" ? { name: item } : item))
        .filter((item) => item && item.name);
    },
    productTags() {
      if (Array.isArray(this.productVariant?.tags)) {
        return this.productVariant.tags;
      }
      if (
        typeof this.productVariant?.taglia === "string" &&
        this.productVariant.taglia
      ) {
        return [{ name: this.productVariant.taglia }];
      }
      return [];
    },
    shareLinks() {
      if (!this.shareUrl) {
        return [];
      }
      const encodedUrl = encodeURIComponent(this.shareUrl);
      const mailSubject = encodeURIComponent(
        this.product?.nome || "Check this out"
      );
      const mailBody = encodeURIComponent(
        `${this.product?.nome || ""}\n${this.shareUrl}`
      );
      return [
        {
          name: "Facebook",
          icon: ["fab", "facebook-f"],
          className: "share-facebook",
          url: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
        },
        {
          name: "Twitter",
          icon: ["fab", "twitter"],
          className: "share-twitter",
          url: `https://twitter.com/intent/tweet?url=${encodedUrl}`,
        },
        {
          name: "Pinterest",
          icon: ["fab", "pinterest-p"],
          className: "share-pinterest",
          url: `https://pinterest.com/pin/create/button/?url=${encodedUrl}`,
        },
        {
          name: "LinkedIn",
          icon: ["fab", "linkedin-in"],
          className: "share-linkedin",
          url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
        },
        {
          name: "Email",
          icon: ["fas", "envelope"],
          className: "share-email",
          url: `mailto:?subject=${mailSubject}&body=${mailBody}`,
        },
      ];
    },
  },
  watch: {
    $route() {
      this.loadProduct();
    },
  },
  async created() {
    await this.loadProduct();
  },
  methods: {
    async loadProduct() {
      this.loading = true;
      this.productVariant = {};
      this.product = {};
      this.productAllVariant = [];
      this.auctions = [];
      this.bidDetail = {};
      this.latestAuctionOffers = [];
      await this.getProducts();
      await this.getBidDetail();
      if (
        this.$store.getters["user/LoggedInUserType"] === LOGGED_IN_USER &&
        this.productType === "auction"
      ) {
        await this.getLatestAuctionOffers();
      }
      this.setPageMetadata();
      this.updateShareUrl();
      this.val = this.productType === "auction" ? 0 : 1;
      this.loading = false;
    },
    setPageMetadata() {
      if (this.product?.nome) {
        document.title = `${this.product.nome} - viabrera`;
        this.$store.commit("setLocation", [
          { title: "Products", location: "/shop" },
          {
            title: this.product.nome,
            location: `/product/${this.product._id}-${this.productVariant?._id}`,
          },
        ]);
      }
    },
    updateShareUrl() {
      if (typeof window !== "undefined") {
        this.shareUrl = window.location.href;
      }
    },
    async getBidDetail() {
      if (!this.astaId) return;
      const res = await getBidDetailService({
        astaId: this.astaId,
      });
      this.bidDetail = res.asta || {};
    },
    async getProducts() {
      const res = await getAllProductsAndVariants();
      const variants = res?.varianti ?? [];
      this.allProducts = variants;
      const routeParam = this.$route.params.productID || "";
      const [productId, variantId] = routeParam.split("-");
      if (!productId || !variantId) {
        return;
      }
      this.productAllVariant = variants.filter(
        (item) => item?._prodotto?.[0]?._id === productId
      );
      this.productVariant =
        this.productAllVariant.find((variant) => variant?._id === variantId) ||
        {};
      this.product = this.productVariant?._prodotto?.[0] || {};
      this.imgs = this.productVariant?.immagine || [];

      this.auctions = variants.filter(
        (variant) =>
          Array.isArray(variant._astaCorrente) &&
          variant._astaCorrente.length > 0
      );
      const auctionProduct = this.getAuctionProduct();
      this.astaId = auctionProduct?._astaCorrente?.[0] || "";
      this.productType = auctionProduct ? "auction" : "single";
    },
    getAuctionProduct() {
      return this.auctions.find(
        (auction) =>
          auction?._id === this.productVariant?._id &&
          auction?._prodotto?.[0]?._id === this.product?._id
      );
    },
    async placeBid() {
      try {
        const bidAmount = this.val;
        const currentBidAmount = this.latestAuctionOffers?.[0]?.prezzo ?? 0;

        if (bidAmount <= currentBidAmount) {
          this.$toast.error("Bid amount must be higher than the current bid!");
          return;
        }

        if (!this.astaId) {
          this.$toast.error("Auction has ended or not available.");
          return;
        }

        const res = await addBidService({
          astaId: this.astaId,
          valoreOfferta: bidAmount,
        });

        if (!res || res.error) {
          this.$toast.error("Failed to place the bid.");
        } else {
          await this.getLatestAuctionOffers();
          this.$toast.success("Bid placed successfully!");
        }
      } catch (error) {
        this.$toast.error("Something went wrong. Please try again later.");
      }
    },
    async getLatestAuctionOffers() {
      if (!this.astaId) return;
      const res = await getLatestAuctionOffersService({ astaId: this.astaId });
      this.latestAuctionOffers = res.offerte || [];
    },
    handleAddToCart() {
      const qty = this.val;
      if (!(qty > 0)) {
        this.$toast.error("Please select quantity!");
        return;
      }
      addToCart(this.product._id, this.productVariant._id, qty);
    },
  },
};
</script>

<style scoped>
.product-page {
  padding: 40px 0 120px;
}

.product-header {
  padding: 48px 0 32px;
  border-bottom: 1px solid #e5e5e5;
  margin-bottom: 48px;
}

.product-breadcrumb {
  margin-bottom: 16px;
}

.product-title {
  font-size: 36px;
  font-weight: 700;
  color: #1d1d1d;
  margin: 0;
}

.product-subtitle {
  margin: 8px 0 0;
  font-size: 18px;
  color: #7b7b7b;
}

.product-main {
  row-gap: 40px;
}

.product-gallery-card {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 20px 45px rgba(0, 0, 0, 0.08);
  padding: 24px;
}

.product-summary-card {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 20px 45px rgba(0, 0, 0, 0.08);
  padding: 32px;
  display: flex;
  flex-direction: column;
  gap: 28px;
}

.summary-price-block {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.summary-label {
  text-transform: uppercase;
  font-size: 12px;
  letter-spacing: 0.12em;
  color: #8b8b8b;
}

.summary-value {
  font-size: 28px;
  font-weight: 700;
  color: #1d1d1d;
}

.summary-description {
  color: #4f4f4f;
  font-size: 16px;
  line-height: 1.7;
}

.summary-meta {
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.summary-meta__item {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  align-items: center;
}

.meta-value {
  font-weight: 600;
  color: #1d1d1d;
}

.meta-tags {
  flex: 1;
}

.summary-actions {
  display: flex;
  align-items: flex-end;
  gap: 20px;
  flex-wrap: wrap;
}

.summary-quantity {
  min-width: 140px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.summary-action-icons {
  display: flex;
  align-items: center;
  gap: 12px;
}

.summary-share {
  display: flex;
  align-items: center;
  gap: 16px;
}

.share-list {
  display: flex;
  gap: 10px;
  list-style: none;
  margin: 0;
  padding: 0;
}

.share-icon {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  display: grid;
  place-items: center;
  color: #fff;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.share-icon:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 20px rgba(0, 0, 0, 0.15);
}

.share-facebook {
  background-color: #4064ac;
}

.share-twitter {
  background-color: #1da1f2;
}

.share-pinterest {
  background-color: #e60023;
}

.share-linkedin {
  background-color: #2867b2;
}

.share-email {
  background-color: #242424;
}

.summary-countdown {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  border-radius: 8px;
  background: #f7f7f7;
}

.summary-countdown__label {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.summary-countdown__date {
  font-size: 14px;
}

.summary-counter :deep(.Counter) {
  margin: 0;
}

:deep(.mySwiper2) {
  border-radius: 6px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
}

:deep(.mySwiper) {
  margin-top: 18px;
}

:deep(.mySwiper .swiper-slide) {
  border-radius: 6px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.05);
}

:deep(.mySwiper img) {
  object-fit: cover;
  width: 100%;
  height: 100%;
}

.product-tabs {
  margin-top: 80px;
}

.related-products {
  margin: 90px 0 40px;
}

.related-products__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 32px;
}

.related-products__title {
  margin: 0;
  font-size: 26px;
  font-weight: 700;
  color: #1d1d1d;
}

.related-products__list {
  row-gap: 24px;
}

:deep(.meta-tags .item) {
  font-weight: 600 !important;
  color: #1d1d1d !important;
}

.meta-label {
  color: #000 !important
}

@media (max-width: 991.98px) {
  .product-header {
    padding: 32px 0 24px;
    margin-bottom: 32px;
  }

  .product-title {
    font-size: 30px;
  }

  .product-summary-card,
  .product-gallery-card {
    padding: 24px;
  }
}

@media (max-width: 575.98px) {
  .product-page {
    padding: 24px 0 80px;
  }

  .product-title {
    font-size: 26px;
  }

  .product-summary-card {
    gap: 20px;
    padding: 20px;
  }

  .summary-actions {
    gap: 16px;
    flex-direction: column;
    align-items: stretch;
  }

  .summary-action-icons {
    justify-content: space-between;
  }

  .summary-share {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>

<style>
  .loader-wrapper {
    min-width: 100vw;
    min-height: 100vh;
  }
  .loader-wrapper .loader {
    border: 4px solid #f3f3f3;
    border-top: 4px solid #3498db;
    border-radius: 50%;
    width: 40px;
    height: 40px;
    animation: spin 1s linear infinite;
    margin: 100px auto;
  }
  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
</style>
